import type { ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { FolderX } from "lucide-react";
import { Button } from "components/common/buttons/Button";
import { EmptyState } from "components/common/EmptyState/EmptyState";
import { useProjectIdentifiers } from "store/hooks/useProjectIdentifiers";
import { getErrorStatus } from "types/error.types";

const MISSING_PROJECT_STATUSES = new Set([403, 404]);

/**
 * Project pages each read the project. When that read fails, a page that mounts after the
 * failure starts the read again, which puts the query back into loading and remounts the
 * page's loader: an endless request loop. The gate shows the failure instead of the page.
 */
export const ProjectLoadGate = ({ children }: { children: ReactNode }) => {
  const { projectId: routeProjectId, workspaceId } = useParams<{
    projectId: string;
    workspaceId: string;
  }>();
  const { project, isProjectError, projectError, refetchProject } = useProjectIdentifiers();

  if (!routeProjectId || project || !isProjectError) {
    return <>{children}</>;
  }

  const projectsHref = workspaceId ? `/workspaces/${workspaceId}/projects` : "/";
  const status = getErrorStatus(projectError);

  if (status != null && MISSING_PROJECT_STATUSES.has(status)) {
    return (
      <section
        aria-label="Project unavailable"
        className="flex h-full min-h-[calc(100svh-54px)] items-center justify-center p-8"
      >
        <EmptyState
          icon={<FolderX aria-hidden />}
          title="Project not available"
          description="This project does not exist, or this account does not have access to it."
          accent="gray"
          action={
            <Button asChild color="gray" size="sm">
              <Link to={projectsHref}>Back to all projects</Link>
            </Button>
          }
        />
      </section>
    );
  }

  return (
    <section
      aria-label="Project unavailable"
      className="flex h-full min-h-[calc(100svh-54px)] items-center justify-center p-8"
    >
      <EmptyState
        icon={<FolderX aria-hidden />}
        title="Could not load this project"
        description="Check the connection and try again."
        accent="gray"
        action={
          <Button type="button" color="gray" size="sm" onClick={() => void refetchProject()}>
            Try again
          </Button>
        }
      />
    </section>
  );
};
